import * as React from "react";
import { useSearchParams } from "react-router";

import { useSearchQuery } from "@/api/files";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import type { SortOption } from "@/components/sortDropdown";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useSortedItems } from "@/hooks/use-sorted-items";
import type { FolderItem, Item } from "@/types/fse";
import { useDriveList } from "@/views/drive/hooks/use-drive-list";
import { usePagination } from "@/views/drive/hooks/use-paginations";
import { mapSearchModeToApi, type SearchMode } from "@/views/drive/search/types";
import {
  DRIVE_PAGE_SIZE,
  DRIVE_SCOPE_OWNED_VALUE,
  DRIVE_SCOPE_PARAM,
  UNIFIED_DRIVE_SORT_STORAGE_KEY,
} from "./constants";
import type { DirectoryUsersById, DriveScope, DriveScopeCounts } from "./types";
import {
  collectDirectoryUserIds,
  extractRootItems,
  filterOwnedSearchResults,
  mergeRootItems,
  paginateDriveItems,
  sortDriveItems,
} from "./utils";

export interface UseUnifiedDriveWorkspaceArgs {
  initialScope: DriveScope;
}

interface PartialError {
  source: "owned" | "shared";
  retry: () => unknown;
}

interface FatalError {
  retry: () => unknown;
}

export interface UnifiedDriveWorkspaceResult {
  authenticatedUserId: string;
  scope: DriveScope;
  query: string;
  counts: DriveScopeCounts;
  rootFolder?: FolderItem;
  rows: Item[];
  visibleRows: Item[];
  usersById: DirectoryUsersById;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentSort: SortOption;
  setCurrentSort: ReturnType<typeof useSortedItems>["setCurrentSort"];
  isLoading: boolean;
  isFetching: boolean;
  partialError: PartialError | null;
  fatalError: FatalError | null;
}

const getDriveScope = (searchParams: URLSearchParams, initialScope: DriveScope): DriveScope => {
  const value = searchParams.get(DRIVE_SCOPE_PARAM);
  if (value === DRIVE_SCOPE_OWNED_VALUE) return "owned";
  return initialScope;
};

export const useUnifiedDriveWorkspace = ({
  initialScope,
}: UseUnifiedDriveWorkspaceArgs): UnifiedDriveWorkspaceResult => {
  const authenticatedUser = useAuthenticatedUser();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const mode = searchParams.get("mode") as SearchMode | null;
  const scope = getDriveScope(searchParams, initialScope);
  const ownedRootQuery = useDriveList();
  const sharedRootQuery = useDriveList({ only_shared: true });
  const searchQuery = useSearchQuery(
    { query, only_shared: scope === "shared", search_mode: mapSearchModeToApi(mode) },
    { skip: query.length === 0 },
  );
  const ownedRows = extractRootItems(ownedRootQuery.data);
  const sharedRows = extractRootItems(sharedRootQuery.data);
  const rootRows = mergeRootItems(ownedRootQuery.data, sharedRootQuery.data);
  const searchRows = searchQuery.data ?? [];
  const scopedRows = query
    ? scope === "owned"
      ? filterOwnedSearchResults(searchRows, authenticatedUser.id)
      : searchRows
    : scope === "owned"
      ? ownedRows
      : scope === "shared"
        ? sharedRows
        : rootRows;
  const { sortedItems, currentSort, setCurrentSort } = useSortedItems(
    scopedRows,
    UNIFIED_DRIVE_SORT_STORAGE_KEY,
    sortDriveItems,
  );
  const pagination = usePagination(sortedItems, DRIVE_PAGE_SIZE);
  const { currentPage, itemsPerPage, setCurrentPage, totalPages } = pagination;
  const visibleRows = paginateDriveItems(sortedItems, currentPage, itemsPerPage);
  const directoryUserIds = collectDirectoryUserIds(visibleRows, authenticatedUser.id);
  const directoryQuery = useGetDirectoryUsersQuery(directoryUserIds, { skip: directoryUserIds.length === 0 });
  const usersById = React.useMemo(
    () => new Map((directoryQuery.data ?? []).map(user => [user.id, user])),
    [directoryQuery.data],
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [scope, query, mode, currentSort, setCurrentPage]);

  const fatalError =
    query.length > 0 && searchQuery.error
      ? { retry: searchQuery.refetch }
      : ownedRootQuery.error && sharedRootQuery.error
        ? { retry: () => Promise.all([ownedRootQuery.refetch(), sharedRootQuery.refetch()]) }
        : null;
  const partialError = fatalError
    ? null
    : ownedRootQuery.error
      ? { source: "owned" as const, retry: ownedRootQuery.refetch }
      : sharedRootQuery.error
        ? { source: "shared" as const, retry: sharedRootQuery.refetch }
        : null;

  return {
    authenticatedUserId: authenticatedUser.id,
    scope,
    query,
    counts: { all: ownedRows.length + sharedRows.length, owned: ownedRows.length, shared: sharedRows.length },
    rootFolder: ownedRootQuery.data?.type === "folder" ? ownedRootQuery.data : undefined,
    rows: sortedItems,
    visibleRows,
    usersById,
    currentPage,
    totalPages,
    itemsPerPage,
    setCurrentPage,
    currentSort,
    setCurrentSort,
    isLoading: ownedRootQuery.isLoading || sharedRootQuery.isLoading,
    isFetching: ownedRootQuery.isFetching || sharedRootQuery.isFetching || (query.length > 0 && searchQuery.isFetching),
    partialError,
    fatalError,
  };
};
